// Resolution order:
//   1. public/config.js  — editable on the server, no rebuild needed
//   2. REACT_APP_API_URL — compiled in at build time (.env / .env.production)
//   3. same origin       — when the API is reverse-proxied under /api on this host
function resolveBaseUrl() {
  const runtime =
    typeof window !== 'undefined' &&
    window.__SCHOOLRUNS_CONFIG__ &&
    typeof window.__SCHOOLRUNS_CONFIG__.apiUrl === 'string'
      ? window.__SCHOOLRUNS_CONFIG__.apiUrl.trim()
      : '';
  const url = runtime || process.env.REACT_APP_API_URL || '';
  return url.replace(/\/+$/, ''); // never leave a trailing slash
}

const BASE_URL = resolveBaseUrl();

/**
 * Resolve a server-relative path (e.g. the '/uploads/123.jpg' stored in
 * photoUrl) against the API origin, so <img src> works regardless of which
 * host the panel itself is served from.
 */
export function absoluteUrl(path) {
  const p = String(path || '');
  if (!p) return '';
  return /^https?:\/\//i.test(p) ? p : `${BASE_URL}${p.startsWith('/') ? '' : '/'}${p}`;
}

// ── Token management ────────────────────────────────────────────────
let _token = localStorage.getItem('admin_token') || null;

export function setToken(token) {
  _token = token;
  if (token) {
    localStorage.setItem('admin_token', token);
  } else {
    localStorage.removeItem('admin_token');
  }
}

export function getToken() {
  return _token;
}

// ── HTTP helpers ────────────────────────────────────────────────────
function headers(extra = {}) {
  const h = { 'Content-Type': 'application/json', ...extra };
  if (_token) {
    h['Authorization'] = `Bearer ${_token}`;
  }
  return h;
}

async function request(method, path, body, customHeaders) {
  try {
    const opts = {
      method,
      headers: customHeaders || headers(),
    };
    if (body !== undefined && body !== null) {
      opts.body = JSON.stringify(body);
    }
    const res = await fetch(`${BASE_URL}${path}`, opts);
    const data = await res.json();
    return data;
  } catch (err) {
    console.error(`API ${method} ${path} failed:`, err);
    return { success: false, error: err.message || 'Network error' };
  }
}

const get = (path) => request('GET', path);
const post = (path, body) => request('POST', path, body);
const put = (path, body) => request('PUT', path, body);
const patch = (path, body) => request('PATCH', path, body);
const del = (path) => request('DELETE', path);

// ── Query-string builder ────────────────────────────────────────────
// Drops undefined / null / '' values and URL-encodes the rest.
// qs({ area: 'Grays', name: '' }) -> '?area=Grays'
export function qs(params) {
  if (!params || typeof params !== 'object') return '';
  const parts = [];
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '') return;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  });
  return parts.length ? `?${parts.join('&')}` : '';
}

// ── Auth API ────────────────────────────────────────────────────────
export const authApi = {
  adminLogin: async (email, password) => {
    const result = await post('/api/auth/admin-login', { email, password });
    if (result.success && result.token) {
      setToken(result.token);
    }
    return result;
  },
};

// ── Driver API ──────────────────────────────────────────────────────
export const driverApi = {
  getAll: (filters) => get(`/api/drivers${qs(filters)}`),
  getById: (id) => get(`/api/drivers/${id}`),
  create: (driverData) => post('/api/drivers', driverData),
  update: (id, driverData) => put(`/api/drivers/${id}`, driverData),
  delete: (id) => del(`/api/drivers/${id}`),
  updateRouteStatus: (id, status) =>
    patch(`/api/drivers/${id}/status`, { routeStatus: status }),
};

// ── Escort API ──────────────────────────────────────────────────────
export const escortApi = {
  getAll: (filters) => get(`/api/escorts${qs(filters)}`),
  getById: (id) => get(`/api/escorts/${id}`),
  create: (escortData) => post('/api/escorts', escortData),
  update: (id, escortData) => put(`/api/escorts/${id}`, escortData),
  delete: (id) => del(`/api/escorts/${id}`),
  updateRouteStatus: (id, status) =>
    patch(`/api/escorts/${id}/status`, { routeStatus: status }),
  /** Drivers nearest to this PA, sorted by distance. */
  nearestDrivers: (id, params) => get(`/api/escorts/${id}/nearest-drivers${qs(params)}`),
};

// ── Route API ───────────────────────────────────────────────────────
export const routeApi = {
  getAll: (filters) => get(`/api/routes${qs(filters)}`),
  getById: (id) => get(`/api/routes/${id}`),
  create: (routeData) => post('/api/routes', routeData),
  update: (id, routeData) => put(`/api/routes/${id}`, routeData),
  delete: (id) => del(`/api/routes/${id}`),
  assign: (id, body) => post(`/api/routes/${id}/assign`, body),
  unassign: (id, body) => post(`/api/routes/${id}/unassign`, body || {}),
  nearest: (id, radius, limit) =>
    get(`/api/routes/${id}/nearest${qs({ radius, limit })}`),
};

// ── Dashboard API ───────────────────────────────────────────────────
export const dashboardApi = {
  getStats: () => get('/api/dashboard/stats'),
  getFilters: () => get('/api/dashboard/filters'),
  getCoverage: (radius, limit) =>
    get(`/api/dashboard/coverage${qs({ radius, limit })}`),
};

// ── Geo API ─────────────────────────────────────────────────────────
export const geoApi = {
  lookup: (postCode) => post('/api/geo/lookup', { postCode }),
};

// ── Areas API ───────────────────────────────────────────────────────
export const areaApi = {
  /** Canonical area reference list, grouped by borough. */
  getAll: (params) => get('/api/areas' + qs(params)),
};

// ── Assignment API ──────────────────────────────────────────────────
export const assignmentApi = {
  assignEscortToDriver: (driverId, escortId) =>
    post('/api/assignments/assign', { driverId, escortId }),
  unassignEscort: (driverId) =>
    post('/api/assignments/unassign', { driverId }),
};

// ── Upload API ──────────────────────────────────────────────────────
export const uploadApi = {
  upload: async (formData) => {
    try {
      const h = {};
      if (_token) {
        h['Authorization'] = `Bearer ${_token}`;
      }
      // Do NOT set Content-Type — browser sets multipart boundary automatically
      const res = await fetch(`${BASE_URL}/api/upload`, {
        method: 'POST',
        headers: h,
        body: formData,
      });
      return await res.json();
    } catch (err) {
      console.error('Upload failed:', err);
      return { success: false, error: err.message || 'Upload failed' };
    }
  },
};
